import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

// material-ui
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

// project imports
import OtherBehaviorsSummary from 'components/OtherBehaviorsSummary';
import { behaviorDisplayName, intervalRecordings, useBehaviorNames } from 'utils/behaviorInterval';

/**
 * Read-only view of a Behavior Interval sheet: EVERY recording on it (a session can
 * record several behaviours, newest first), each with its behaviour name, interval
 * type, W / P / N / missed tallies, the interval grid and observer notes.
 *
 * Shared by the session-data dialog and the therapist calendar so the two can never
 * drift again - the calendar kept a single-recording copy that showed only the
 * latest recording and printed reference behaviours as their raw id.
 */
const BehaviorIntervalRecordingsView = ({ raw }) => {
  const intl = useIntl();
  const theme = useTheme();
  const names = useBehaviorNames();
  const nameOf = (rec) => behaviorDisplayName(rec.behavior, rec.behaviorLabel, names);

  // Every recording on the sheet (a session can record several behaviours).
  const recordings = intervalRecordings(raw);
  const multi = recordings.length > 1;
  return (
    <Stack spacing={2.5}>
      {recordings.map((rec, ri) => {
        const data = rec.data || [];
        return (
          <Stack key={rec.id || ri} spacing={2} sx={multi ? { p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 } : undefined}>
            {multi && (
              <Typography variant="subtitle2">
                {intl.formatMessage(
                  { id: 'form.behaviorInterval.recordingN', defaultMessage: 'Recording {n}' },
                  { n: recordings.length - ri }
                )}
                {rec._inProgress
                  ? ` (${intl.formatMessage({ id: 'form.behaviorInterval.inProgress', defaultMessage: 'in progress' })})`
                  : ''}
              </Typography>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={`Behavior: ${nameOf(rec) || '—'}`} />
              <Chip
                size="small"
                label={`Type: ${rec.intervalType || '—'}${rec.intervalType === 'momentary' && rec.momentSecond ? ` @ ${rec.momentSecond}s` : ''}`}
              />
              <Chip size="small" label={`Interval ${rec.intervalLength ?? '—'}s`} />
              <Chip size="small" label={`Total ${rec.totalIntervals ?? data.length}`} variant="outlined" />
              {rec.wholeCount != null && (
                <Chip size="small" color="success" label={`Whole ${rec.wholeCount} (${rec.wholePercent ?? 0}%)`} />
              )}
              {rec.partialCount != null && (
                <Chip size="small" color="warning" label={`Partial ${rec.partialCount} (${rec.partialPercent ?? 0}%)`} />
              )}
              {rec.noneCount != null && <Chip size="small" label={`None ${rec.noneCount} (${rec.nonePercent ?? 0}%)`} />}
              {rec.missedCount != null && (
                <Chip size="small" color="error" label={`Missed ${rec.missedCount} (${rec.missedPercent ?? 0}%)`} />
              )}
            </Stack>
            {data.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  {intl.formatMessage({ id: 'form.behaviorInterval.intervalGrid', defaultMessage: 'Interval grid' })}
                </Typography>
                <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.5}>
                  {data.map((v, i) => {
                    const labelMap = { whole: 'W', part: 'P', none: 'N', missed: '—' };
                    const colorMap = {
                      whole: 'success.main',
                      part: 'warning.main',
                      none: 'grey.400',
                      missed: 'error.main'
                    };
                    // Other behaviours ticked for this interval: a dot on the cell,
                    // their names in its tooltip.
                    const others = (Array.isArray(rec.otherBehaviors?.[i]) ? rec.otherBehaviors[i] : []).filter(
                      (b) => b && b !== rec.behavior
                    );
                    const otherNames = others.map((b) => rec.otherBehaviorLabels?.[b] || behaviorDisplayName(b, null, names)).join(', ');
                    return (
                      <Box
                        key={i}
                        sx={{
                          position: 'relative',
                          width: 28,
                          height: 28,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid',
                          borderColor: others.length ? 'info.main' : 'divider',
                          borderRadius: 0.5,
                          bgcolor: v
                            ? alpha(theme.palette[colorMap[v]?.split('.')[0]]?.[colorMap[v]?.split('.')[1]] || '#999', 0.18)
                            : 'transparent',
                          color: v ? colorMap[v] : 'text.disabled',
                          fontWeight: 700,
                          fontSize: '0.75rem'
                        }}
                        title={`Interval ${i + 1}: ${v || 'empty'}${otherNames ? ` · also: ${otherNames}` : ''}`}
                      >
                        {labelMap[v] ?? ''}
                        {others.length > 0 && (
                          <Box
                            component="span"
                            sx={{
                              position: 'absolute',
                              top: 1,
                              right: 1,
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: 'info.main'
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}
            <OtherBehaviorsSummary record={rec} />
            {rec.notes && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {intl.formatMessage({ id: 'form.behaviorInterval.observerNotes', defaultMessage: 'Observer Notes' })}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {rec.notes}
                </Typography>
              </Box>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};

BehaviorIntervalRecordingsView.propTypes = {
  // raw_data of a behavior-interval record
  raw: PropTypes.object
};

export default BehaviorIntervalRecordingsView;
